/**
 * Finding the paragraph a redline belongs to after the document was edited,
 * from what was remembered about that paragraph when the redline was anchored.
 *
 * This is the deterministic half of re-anchoring; the core's LLM locate is the
 * other half and runs only when this one answers ambiguous or none. The web
 * route `locate-paragraph` runs it for the core; the add-in's own recovery
 * steps use the same `TextSimilarity`, so the two sides agree on what "still
 * the same paragraph" means.
 *
 * Tiers, in the order tried, each stopping at a unique answer:
 *
 *   1. known id      the paragraph with the remembered `paraId`, if its text
 *                    still reads as remembered, or drifted but is confirmed by
 *                    its context or similar enough;
 *   2. bracket       the paragraph between the remembered neighbours' ids, by
 *                    exact text, by containment, or by context plus similarity;
 *   3. section       the text tiers below, restricted to paragraphs under the
 *                    remembered heading when that heading still exists;
 *   4. text tiers    exact text, then exact text confirmed by context, then the
 *                    best fuzzy match supported by context;
 *   5. semantic      the paragraph an embedding says is a paraphrase, for a
 *                    rewording the word-level tiers cannot see. The core sends
 *                    the similarities with the request; this side never computes
 *                    them and never scores them into the rank gate;
 *   6. rank gate     a weighted score over every candidate, accepted only when
 *                    one clearly leads; otherwise the ranking is returned as an
 *                    ambiguous answer for the LLM to arbitrate.
 *
 * Each paragraph is searched in two layers: its accepted text and its original
 * text (every tracked change rejected), so a paragraph whose text was struck
 * is still found.
 */
import { z } from "zod";

import { TextSimilarity } from "./text/text-similarity";
import { WordTokenizer } from "./text/word-tokenizer";

export const resolutionPathSchema = z.enum([
    "id_unchanged",
    "id_text_changed",
    "neighbour_bracket",
    "text_tier_exact",
    "text_tier_context",
    "text_tier_fuzzy",
    "text_tier_semantic",
    "text_tier_llm",
    "rank_gate",
]);
export type ResolutionPath = z.infer<typeof resolutionPathSchema>;

export const documentLayerSchema = z.enum(["accepted", "original"]);
export type DocumentLayer = z.infer<typeof documentLayerSchema>;

/** A paragraph next to the anchored one, as remembered when the redline was anchored. */
export const neighbourParagraphSchema = z.object({
    paraId: z.string().nullable(),
    textId: z.string().nullable(),
    text: z.string(),
});
export type NeighbourParagraph = z.infer<typeof neighbourParagraphSchema>;

/** What was remembered about one paragraph the redline covers; every field but `text` may be unknown. */
export const paragraphAnchorSchema = z.object({
    paraId: z.string().nullable(),
    textId: z.string().nullable(),
    text: z.string(),
    /**
     * The words just before and after the paragraph when it was anchored. The locator
     * compares the last and the first `CONTEXT_WINDOW_WORDS` of them, cut as the redline
     * diff cuts words, so a context the add-in sent as whitespace words (a Japanese
     * paragraph is one) is compared in its own words.
     */
    contextBefore: z.string(),
    contextAfter: z.string(),
    headingPath: z.array(z.string()).nullable(),
    paragraphIndex: z.number().int().nullable(),
    /** How far through the document the paragraph sat, 0 to 100, when known. */
    positionPct: z.number().nullable(),
    neighbourAbove: neighbourParagraphSchema.nullable(),
    neighbourBelow: neighbourParagraphSchema.nullable(),
});
export type ParagraphAnchor = z.infer<typeof paragraphAnchorSchema>;

/** One anchor per paragraph the redline covers, in document order. */
export const spanAnchorSchema = z.object({
    members: z.array(paragraphAnchorSchema).min(1),
});
export type SpanAnchor = z.infer<typeof spanAnchorSchema>;

/** A live paragraph as the locator searches it; `headingPath` comes from the core's heading detection. */
export const locatableParagraphSchema = z.object({
    paragraphIndex: z.number().int().nonnegative(),
    allChangesAcceptedText: z.string(),
    allChangesRejectedText: z.string(),
    paraId: z.string().nullable(),
    textId: z.string().nullable(),
    headingPath: z.array(z.string()).nullable(),
});
export type LocatableParagraph = z.infer<typeof locatableParagraphSchema>;

/**
 * One embedding similarity, for one span member against one paragraph in one
 * layer. The core computes these with the model it ships and sends them with
 * the request; the `original` layer arrives only for paragraphs whose original
 * text differs from their accepted text, so an absent entry means "not
 * measured", not "measured as zero".
 */
export const semanticSimilaritySchema = z.object({
    memberIndex: z.number().int().nonnegative(),
    paragraphIndex: z.number().int().nonnegative(),
    layer: documentLayerSchema,
    similarity: z.number(),
});
export type SemanticSimilarity = z.infer<typeof semanticSimilaritySchema>;

export const locateParagraphRequestSchema = z.object({
    paragraphs: z.array(locatableParagraphSchema),
    anchor: spanAnchorSchema,
    semanticSimilarities: z.array(semanticSimilaritySchema),
});
export type LocateParagraphRequest = z.infer<typeof locateParagraphRequestSchema>;

export const candidateSignalsSchema = z.object({
    idMatches: z.boolean(),
    textSimilarity: z.number(),
    contextBeforeMatches: z.boolean().nullable(),
    contextAfterMatches: z.boolean().nullable(),
    headingPathMatches: z.boolean().nullable(),
    positionDeltaPct: z.number().nullable(),
});
export type CandidateSignals = z.infer<typeof candidateSignalsSchema>;

export const rankedParagraphCandidateSchema = z.object({
    paragraphIndex: z.number().int(),
    layer: documentLayerSchema,
    score: z.number(),
    signals: candidateSignalsSchema,
});
export type RankedParagraphCandidate = z.infer<typeof rankedParagraphCandidateSchema>;

export const paragraphLocationSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("resolved"), paragraphIndex: z.number().int(), layer: documentLayerSchema, resolutionPath: resolutionPathSchema }),
    z.object({ status: z.literal("ambiguous"), presentation: z.enum(["ranked", "tie"]), candidates: z.array(rankedParagraphCandidateSchema) }),
    z.object({ status: z.literal("none") }),
]);
export type ParagraphLocation = z.infer<typeof paragraphLocationSchema>;

export const spanLocationSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("resolved"), paragraphIndexes: z.array(z.number().int()), layer: documentLayerSchema, resolutionPath: resolutionPathSchema }),
    z.object({
        status: z.literal("broken"),
        locatedParagraphIndexes: z.array(z.number().int()),
        unresolvedMemberIndexes: z.array(z.number().int()),
        reason: z.enum(["non_contiguous", "member_unresolved"]),
    }),
    z.object({ status: z.literal("none") }),
]);
export type SpanLocation = z.infer<typeof spanLocationSchema>;

/**
 * Words of context kept either side of a paragraph; enough to tell repeated boilerplate
 * apart, short enough to survive small edits nearby. A word is a unit of the redline diff
 * (`WordTokenizer.diffUnits`), so a Japanese, Chinese or Thai context is eight of its
 * words, as an English one is eight of its.
 */
export const CONTEXT_WINDOW_WORDS = 8;

/** Whether the remembered context around a candidate is found around it now; null on a side with no remembered context. */
interface ContextMatch {
    before: boolean | null;
    after: boolean | null;
}

interface ScoredCandidate {
    paragraphIndex: number;
    layer: DocumentLayer;
    text: string;
    idMatches: boolean;
    exact: boolean;
    similarity: number;
    context: ContextMatch;
    headingPathMatches: boolean | null;
    positionDeltaPct: number | null;
    /** The embedding similarity the core measured for this member, paragraph and layer; null when it sent none. */
    semantic: number | null;
}

interface TierHit {
    paragraphIndex: number;
    layer: DocumentLayer;
    resolutionPath: ResolutionPath;
}

/** The eight words before and after every paragraph, per layer, as comparison keys joined by spaces, computed once per document. */
interface LayerWindows {
    before: string[];
    after: string[];
}

export class ParagraphLocator {
    /** Similarity at which a paragraph is a candidate at all, and the drift a known id may show and still be trusted. */
    static readonly FUZZY_CANDIDATE_FLOOR = 0.5;
    /** Lead the best fuzzy candidate needs over the runner-up. */
    static readonly FUZZY_UNIQUENESS_MARGIN = 0.1;
    /** A lone fuzzy candidate with no identity behind it must be this close to be accepted. */
    static readonly FUZZY_SINGLE_CANDIDATE_FLOOR = 0.9;
    /** The embedding similarity at which a paraphrase counts as a candidate at all. */
    static readonly SEMANTIC_CANDIDATE_FLOOR = 0.8;
    /**
     * Lead the best semantic candidate needs over the runner-up. Set after the
     * seven landings this tier produced in the clock 4 runs (results
     * 2026-09-07) all fell within 0.182 of a runner-up, so a margin of 0.2
     * returns every one of them as unresolved rather than as a wrong landing.
     */
    static readonly SEMANTIC_UNIQUENESS_MARGIN = 0.2;
    /** Candidates further than this from the remembered position are pruned before ranking, when the position is known. */
    static readonly POSITION_PRUNE_WINDOW_PCT = 60;
    static readonly RANK_VERIFICATION_THRESHOLD = 0.75;
    static readonly RANK_UNIQUENESS_MARGIN = 0.05;
    static readonly SCORE_EPSILON = 1e-6;
    static readonly RANK_SIMILARITY_WEIGHT = 0.45;
    static readonly RANK_CONTEXT_SIDE_WEIGHT = 0.2;
    static readonly RANK_HEADING_WEIGHT = 0.15;
    /** Paragraphs searched either side of a single surviving neighbour. */
    static readonly BRACKET_ONE_SIDED_SPAN = 3;
    /**
     * A remembered text this short is never matched by containment; it would be found inside
     * too many paragraphs. Counted in the redline diff's words (`WordTokenizer.diffUnits`), so a
     * Japanese, Chinese or Thai text is held to the minimum an English one is.
     */
    static readonly CONTAINMENT_MINIMUM_WORDS = 4;

    /** Paths from strongest evidence to weakest; a span takes the weakest of its members'. */
    private static readonly PATH_STRENGTH_ORDER: ResolutionPath[] = [
        "id_unchanged",
        "id_text_changed",
        "neighbour_bracket",
        "text_tier_exact",
        "text_tier_context",
        "text_tier_fuzzy",
        "text_tier_semantic",
        "text_tier_llm",
        "rank_gate",
    ];

    private readonly locatableParagraphs: LocatableParagraph[];
    private readonly acceptedParagraphTexts: string[];
    private readonly allChangesRejectedParagraphTexts: string[];
    private readonly acceptedWindows: LayerWindows;
    private readonly originalWindows: LayerWindows;
    /** Embedding similarities by member, paragraph and layer, so `score` reads one without scanning the array. */
    private readonly semanticByCandidate: Map<string, number>;

    constructor({ paragraphs, semanticSimilarities }: { paragraphs: LocatableParagraph[]; semanticSimilarities: SemanticSimilarity[] }) {
        this.locatableParagraphs = paragraphs;
        this.acceptedParagraphTexts = paragraphs.map((paragraph) => WordTokenizer.collapse(paragraph.allChangesAcceptedText));
        this.allChangesRejectedParagraphTexts = paragraphs.map((paragraph) => WordTokenizer.collapse(paragraph.allChangesRejectedText));
        this.acceptedWindows = windowsOf(this.acceptedParagraphTexts);
        this.originalWindows = windowsOf(this.allChangesRejectedParagraphTexts);
        this.semanticByCandidate = new Map(
            semanticSimilarities.map((entry) => [ParagraphLocator.semanticKey(entry.memberIndex, entry.paragraphIndex, entry.layer), entry.similarity]),
        );
        function windowsOf(texts: string[]): LayerWindows {
            const documentWords: string[] = [];
            const paragraphStartWordIndexes: number[] = [];
            const paragraphEndWordIndexes: number[] = [];
            for (const paragraphText of texts) {
                paragraphStartWordIndexes.push(documentWords.length);
                documentWords.push(...ParagraphLocator.contextWordKeysOf(paragraphText));
                paragraphEndWordIndexes.push(documentWords.length);
            }
            return {
                /**
                 * WHEN building each paragraph's `before` window
                 *   CONSEQUENCE
                 *     Take up to `CONTEXT_WINDOW_WORDS` words immediately before `start`,
                 *     clamped at the document start.
                 *
                 * EXAMPLE
                 *   start  -> 5
                 *   window -> 8
                 *   begin  -> max(0, 5 - 8) = 0
                 *   slice  -> words.slice(0, 5)
                 */
                before: paragraphStartWordIndexes.map((start) => documentWords.slice(Math.max(0, start - CONTEXT_WINDOW_WORDS), start).join(" ")),
                /**
                 * WHEN building each paragraph's `after` window
                 *   CONSEQUENCE
                 *     Take up to `CONTEXT_WINDOW_WORDS` words immediately after `end`.
                 *
                 * EXAMPLE
                 *   end    -> 12
                 *   window -> 8
                 *   slice  -> words.slice(12, 20)
                 */
                after: paragraphEndWordIndexes.map((end) => documentWords.slice(end, end + CONTEXT_WINDOW_WORDS).join(" ")),
            };
        }
    }

    private static semanticKey(memberIndex: number, paragraphIndex: number, layer: DocumentLayer): string {
        return `${memberIndex}:${paragraphIndex}:${layer}`;
    }

    /** Locates every member of the span and reports the span whole, broken, or gone. */
    locateSpan(anchor: SpanAnchor): SpanLocation {
        const memberLocations = anchor.members.map((member, memberIndex) => this.locateMember({ anchor: member, memberIndex }));
        /**
         * CASES
         *   - outcome.status === "resolved" -> keep this outcome
         *   - otherwise                      -> drop it
         *
         * CONSEQUENCE
         *   `resolvedMemberLocations` holds only members that landed on a paragraph.
         *   Missing or ambiguous members do not contribute paragraph indexes.
         */
        const resolvedMemberLocations = memberLocations.flatMap((outcome) => (outcome.status === "resolved" ? [outcome] : []));
        /**
         * WHEN no span member resolved to any live paragraph
         *   CONSEQUENCE
         *     Return `none`.
         *
         * RETURNS
         *   The remembered span could not be recovered anywhere in the live document.
         */
        if (resolvedMemberLocations.length === 0) return { status: "none" };
        const resolvedParagraphIndexes = resolvedMemberLocations.map((outcome) => outcome.paragraphIndex).sort((left, right) => left - right);
        /**
         * CASES
         *   - outcome.status === "resolved" -> []
         *   - otherwise                      -> [memberIndex]
         *
         * EXAMPLE
         *   outcomes statuses -> [resolved, none, ambiguous]
         *   member indexes    -> [0, 1, 2]
         *   result            -> [1, 2]
         */
        const unresolvedMemberIndexes = memberLocations.flatMap((outcome, memberIndex) => (outcome.status === "resolved" ? [] : [memberIndex]));
        /**
         * WHEN at least one span member resolved and at least one did not
         *   CONSEQUENCE
         *     Report `broken` with reason `member_unresolved`.
         *
         * WHY
         *   The span still exists in part, but some remembered sibling paragraphs are gone
         *   or no longer identifiable.
         */
        if (unresolvedMemberIndexes.length > 0) {
            return { status: "broken", locatedParagraphIndexes: resolvedParagraphIndexes, unresolvedMemberIndexes, reason: "member_unresolved" };
        }
        /**
         * CASES
         *   - position === 0                         -> no previous index to compare
         *   - otherwise                              -> require
         *       index === indexes[position - 1] + 1
         *
         * EXAMPLE
         *   indexes    -> [5, 6, 7]
         *   position 1 -> compare 6 to indexes[0] + 1 = 6
         *   position 2 -> compare 7 to indexes[1] + 1 = 7
         */
        const resolvedParagraphIndexesAreContiguous = resolvedParagraphIndexes.every((index, position) => position === 0 || index === resolvedParagraphIndexes[position - 1] + 1);
        /**
         * WHEN every span member resolved but their paragraph indexes are not contiguous
         *   CONSEQUENCE
         *     Report `broken` with reason `non_contiguous`.
         *
         * WHY
         *   The remembered span no longer forms one continuous run of paragraphs.
         */
        if (!resolvedParagraphIndexesAreContiguous) return { status: "broken", locatedParagraphIndexes: resolvedParagraphIndexes, unresolvedMemberIndexes: [], reason: "non_contiguous" };
        /**
         * CASES
         *   - `path` ranks later than `weakest` in `PATH_STRENGTH_ORDER` -> take `path`
         *   - otherwise                                                  -> keep `weakest`
         *
         * WHY
         *   A span reports the weakest evidence any member needed.
         *   One member found only by a later tier downgrades the whole span.
         *
         *
         * EXAMPLE
         *   id_unchanged    index -> 0
         *   text_tier_fuzzy index -> 5
         *   5 > 0                -> weakest becomes `text_tier_fuzzy`
         */
        const weakestPath = resolvedMemberLocations
            .map((outcome) => outcome.resolutionPath)
            .reduce((weakest, path) => (ParagraphLocator.PATH_STRENGTH_ORDER.indexOf(path) > ParagraphLocator.PATH_STRENGTH_ORDER.indexOf(weakest) ? path : weakest));
        const resolvedSpanUsesOriginalLayer = resolvedMemberLocations.some((outcome) => outcome.layer === "original");
        /**
         * CASES
         *   - any located member used layer "original" -> span layer is "original"
         *   - otherwise                                -> span layer is "accepted"
         *
         * WHY
         *   Any member found only in rejected-text form keeps the whole span on the more
         *   conservative layer.
         */
        const resolvedSpanLayer: DocumentLayer = resolvedSpanUsesOriginalLayer ? "original" : "accepted";
        return { status: "resolved", paragraphIndexes: resolvedParagraphIndexes, layer: resolvedSpanLayer, resolutionPath: weakestPath };
    }

    /**
     * Locates one remembered paragraph, running the tiers in the order the
     * header describes. `memberIndex` is the paragraph's position in its span,
     * the index the core keys its semantic similarities by; a lone anchor is
     * member 0 of its own span.
     */
    locateMember({ anchor, memberIndex }: { anchor: ParagraphAnchor; memberIndex: number }): ParagraphLocation {
        /**
         * The last resort: a weighted score over similarity, each confirmed
         * context side and the heading. Only a clear leader is accepted; a tie or a
         * narrow lead is returned as the ranked candidates so the LLM can decide
         * with the text in front of it.
         */
        const rankAndGate = (scored: ScoredCandidate[]): ParagraphLocation => {
            const toRanked = (candidate: ScoredCandidate): RankedParagraphCandidate => {
                return {
                    paragraphIndex: candidate.paragraphIndex,
                    layer: candidate.layer,
                    score: this.rankScore(candidate),
                    signals: {
                        idMatches: candidate.idMatches,
                        textSimilarity: candidate.similarity,
                        contextBeforeMatches: candidate.context.before,
                        contextAfterMatches: candidate.context.after,
                        headingPathMatches: candidate.headingPathMatches,
                        positionDeltaPct: candidate.positionDeltaPct,
                    },
                };
            };
            const positionPrunedCandidates = scored.filter((candidate) => candidate.positionDeltaPct === null || candidate.positionDeltaPct <= ParagraphLocator.POSITION_PRUNE_WINDOW_PCT);
            /**
             * WHEN position pruning removes every candidate
             *   CONSEQUENCE
             *     Return `none`.
             *
             * RETURNS
             *   No candidate remains close enough to the remembered document position.
             */
            if (positionPrunedCandidates.length === 0) return { status: "none" };
            const rankedCandidates = [...positionPrunedCandidates].sort((left, right) => this.rankScore(right) - this.rankScore(left));
            /**
             * EXAMPLE
             *   ranked starts with paragraphs -> [18, 7, 30]
             *   ranked[0]                     -> paragraph 18
             *
             * RETURNS
             *   The highest-ranked candidate's score after descending sort.
             */
            const topScore = this.rankScore(rankedCandidates[0]);
            const topTiedCandidates = rankedCandidates.filter((candidate) => Math.abs(this.rankScore(candidate) - topScore) < ParagraphLocator.SCORE_EPSILON);
            /**
             * WHEN more than one candidate shares the top score within epsilon
             *   CONSEQUENCE
             *     Return `ambiguous` with presentation `tie`.
             *
             * WHY
             *   The rank gate has no unique leader to trust.
             */
            if (topTiedCandidates.length > 1) return { status: "ambiguous", presentation: "tie", candidates: topTiedCandidates.map((candidate) => toRanked(candidate)) };
            /**
             * CASES
             *   - ranked.length > 1 -> use the second-ranked candidate's score
             *   - otherwise         -> 0
             *
             * EXAMPLE
             *   ranked scores -> [0.83, 0.79, 0.61]
             *   ranked[1]     -> the 0.79 candidate
             */
            const runnerUpScore = rankedCandidates.length > 1 ? this.rankScore(rankedCandidates[1]) : 0;
            const topScorePassesRankVerification = topScore >= ParagraphLocator.RANK_VERIFICATION_THRESHOLD;
            const topScoreLeadOverRunnerUp = topScore - runnerUpScore;
            const topScorePassesRankUniqueness = topScoreLeadOverRunnerUp >= ParagraphLocator.RANK_UNIQUENESS_MARGIN;
            /**
             * WHEN the leader clears both rank gates
             *   CASES
             *     - topScore is high enough                -> passes verification
             *     - topScore - runnerUpScore is wide enough -> passes uniqueness
             *
             * CONSEQUENCE
             *   Accept the ranking as a deterministic winner.
             */
            if (topScorePassesRankVerification && topScorePassesRankUniqueness) {
                /**
                 * EXAMPLE
                 *   sorting put paragraph 18 first
                 *   ranked[0].paragraphIndex -> 18
                 *
                 * RETURNS
                 *   The top-ranked candidate, once the rank gate has passed.
                 */
                return { status: "resolved", paragraphIndex: rankedCandidates[0].paragraphIndex, layer: rankedCandidates[0].layer, resolutionPath: "rank_gate" };
            }
            return { status: "ambiguous", presentation: "ranked", candidates: rankedCandidates.map((candidate) => toRanked(candidate)) };
        };
        /**
         * Scores the given paragraphs against the anchor in whichever layer reads
         * closer to the remembered text, keeping only those with some signal: an
         * id, exact text, enough similarity, or confirming context.
         */
        const scoreParagraphCandidates = (anchor: ParagraphAnchor, target: string, paragraphIndexes: number[], idHits: number[], memberIndex: number): ScoredCandidate[] => {
            const paragraphCount = this.locatableParagraphs.length;
            const idHitSet = new Set(idHits);
            const scoredCandidates: ScoredCandidate[] = [];
            for (const paragraphIndex of paragraphIndexes) {
                /**
                 * WHEN the paragraph is one of the anchor's remembered neighbours, and no id of the
                 *      anchor's own paragraph is on it (`isRememberedNeighbourOf`)
                 *   CONSEQUENCE
                 *     it is no candidate, in any tier: the paragraph that stood beside the anchored
                 *     one is never the anchored one. When the user deleted 8.1, the semantic tier
                 *     placed 8.1's card on 8.2, its next neighbour, a clause in the same words; the
                 *     card's words then went against 8.2's (live run, 2026-10-18).
                 */
                if (!idHitSet.has(paragraphIndex) && this.isRememberedNeighbourOf(anchor, target, paragraphIndex)) continue;
                const allChangesAcceptedText = this.acceptedParagraphTexts[paragraphIndex];
                const allChangesRejectedText = this.allChangesRejectedParagraphTexts[paragraphIndex];
                let candidateLayer: DocumentLayer = "accepted";
                let candidateText = allChangesAcceptedText;
                const rejectedTextDiffersFromAcceptedText = allChangesRejectedText !== allChangesAcceptedText;
                const rejectedTextSignalStrength = ParagraphLocator.signalStrength(target, allChangesRejectedText);
                const acceptedTextSignalStrength = ParagraphLocator.signalStrength(target, allChangesAcceptedText);
                const originalTextScoresCloserThanAcceptedText = rejectedTextSignalStrength > acceptedTextSignalStrength;
                const shouldScoreParagraphOnOriginalLayer = rejectedTextDiffersFromAcceptedText && originalTextScoresCloserThanAcceptedText;
                /**
                 * WHEN original text differs from accepted text and scores closer to the target
                 *   CONSEQUENCE
                 *     Score this paragraph on the `allChangesRejectedParagraphTexts` layer instead of `acceptedParagraphTexts`.
                 */
                if (shouldScoreParagraphOnOriginalLayer) {
                    candidateLayer = "original";
                    candidateText = allChangesRejectedText;
                }
                const candidateLayerWindows = candidateLayer === "accepted" ? this.acceptedWindows : this.originalWindows;
                const livePath = this.locatableParagraphs[paragraphIndex].headingPath;
                /**
                 * CASES
                 *   - anchor.headingPath === null -> null
                 *   - livePath === null           -> null
                 *   - otherwise                   -> samePath(livePath, anchor.headingPath)
                 *
                 * NULL
                 *   Heading evidence is unknown, not mismatched.
                 */
                const headingPathMatches = anchor.headingPath === null || livePath === null ? null : ParagraphLocator.headingPathsMatchExactly(livePath, anchor.headingPath);
                const scoredCandidate: ScoredCandidate = {
                    paragraphIndex,
                    layer: candidateLayer,
                    text: candidateText,
                    idMatches: idHitSet.has(paragraphIndex),
                    exact: candidateText === target,
                    similarity: TextSimilarity.score(candidateText, target),
                    context: this.contextAt(anchor, candidateLayerWindows, paragraphIndex),
                    headingPathMatches,
                    /**
                     * CASES
                     *   - anchor.positionPct === null -> null
                     *   - otherwise                   -> absolute drift from current position percent
                     *
                     * EXAMPLE
                     *   paragraphIndex    -> 15
                     *   paragraphCount    -> 60
                     *   current position  -> (100 * 15) / 60 = 25
                     *   anchor.positionPct -> 40
                     *   positionDeltaPct  -> 15
                     */
                    positionDeltaPct: anchor.positionPct === null ? null : Math.abs((100 * paragraphIndex) / paragraphCount - anchor.positionPct),
                    semantic: this.semanticByCandidate.get(ParagraphLocator.semanticKey(memberIndex, paragraphIndex, candidateLayer)) ?? null,
                };
                /**
                 * WHEN a paragraph has any anchor signal at all
                 *   CONSEQUENCE
                 *     Keep it as a scored candidate.
                 *
                 * WHY
                 *   Completely unrelated paragraphs never enter tier selection or ranking.
                 */
                if (hasAnySignal(scoredCandidate)) scoredCandidates.push(scoredCandidate);
            }
            return scoredCandidates;
            function hasAnySignal(candidate: ScoredCandidate): boolean {
                return candidate.idMatches
                    || candidate.exact
                    || candidate.similarity >= ParagraphLocator.FUZZY_CANDIDATE_FLOOR
                    || ParagraphLocator.allRememberedContextSidesMatch(candidate.context)
                    || (candidate.semantic !== null && candidate.semantic >= ParagraphLocator.SEMANTIC_CANDIDATE_FLOOR);
            }
        };
        /** Paragraphs under the remembered heading, or null when the heading is gone or covers the whole document. */
        const paragraphIndexesUnderHeadingPath = (headingPath: string[] | null): number[] | null => {
            if (headingPath === null) return null;
            const paragraphIndexesUnderHeadingPath = this.locatableParagraphs
                .map((paragraph, index) => ({ paragraph, index }))
                .filter(({ paragraph }) => ParagraphLocator.headingPathsMatchExactly(paragraph.headingPath, headingPath))
                .map(({ index }) => index);
            const headingPathMatchesNoParagraphs = paragraphIndexesUnderHeadingPath.length === 0;
            const headingPathMatchesEveryParagraph = paragraphIndexesUnderHeadingPath.length === this.locatableParagraphs.length;
            const headingPathDoesNotNarrowSearch = headingPathMatchesNoParagraphs || headingPathMatchesEveryParagraph;
            /**
             * CASES
             *   - inside.length === 0                      -> heading vanished
             *   - inside.length === this.paragraphs.length -> heading narrows nothing
             *
             * CONSEQUENCE
             *   Return null: the heading is unusable as a search restriction.
             */
            if (headingPathDoesNotNarrowSearch) return null;
            return paragraphIndexesUnderHeadingPath;
        };
        /**
         * The paragraph between the remembered neighbours, when their ids survive:
         * by exact text, then by containment (the paragraph was merged into a
         * neighbour), then by context plus similarity (it was edited in place and
         * re-identified). Each rule must name exactly one paragraph.
         */
        const resolveByBracket = (anchor: ParagraphAnchor, target: string): ParagraphLocation | null => {
            /** The indexes from the surviving neighbour above to the one below, or a few paragraphs past a single surviving neighbour. */
            const bracketRegion = (anchor: ParagraphAnchor): number[] | null => {
                /** An id that appears more than once is trusted nowhere: a copied paragraph carries its source's id. */
                const singleIndexOfParaId = (paraId: string | null): number | null => {
                    const matchingParagraphIndexes = this.indexesOfParaId(paraId);
                    /**
                     * CASES
                     *   - hits.length === 1 -> return hits[0]
                     *   - otherwise         -> return null
                     *
                     * EXAMPLE
                     *   hits     -> [6]
                     *   hits[0]  -> 6
                     *   []       -> null
                     *   [6, 14]  -> null
                     *
                     * NULL
                     *   The id is missing or duplicated, so no single paragraph can be trusted.
                     */
                    return matchingParagraphIndexes.length === 1 ? matchingParagraphIndexes[0] : null;
                };
                const range = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, offset) => from + offset);
                const upperNeighbourParagraphIndex = anchor.neighbourAbove ? singleIndexOfParaId(anchor.neighbourAbove.paraId) : null;
                const lowerNeighbourParagraphIndex = anchor.neighbourBelow ? singleIndexOfParaId(anchor.neighbourBelow.paraId) : null;
                /**
                 * EXAMPLE
                 *   this.paragraphs.length -> 12
                 *   lastIndex              -> 11
                 *
                 * WHY
                 *   Paragraph indexes are zero-based, so the last valid index is count minus one.
                 */
                const lastIndex = this.locatableParagraphs.length - 1;
                /**
                 * WHEN both neighbour ids survived and `upperNeighbourParagraphIndex` is not after `lowerNeighbourParagraphIndex`
                 *   CONSEQUENCE
                 *     Search the full closed interval from `upperNeighbourParagraphIndex` through `lowerNeighbourParagraphIndex`.
                 */
                if (upperNeighbourParagraphIndex !== null && lowerNeighbourParagraphIndex !== null && upperNeighbourParagraphIndex <= lowerNeighbourParagraphIndex) return range(upperNeighbourParagraphIndex, lowerNeighbourParagraphIndex);
                /**
                 * WHEN only the upper neighbour survives
                 *   CONSEQUENCE
                 *     Search downward from that neighbour, capped at the document end.
                 *
                 * EXAMPLE
                 *   above     -> 8
                 *   span      -> 3
                 *   lastIndex -> 10
                 *   end       -> min(11, 10) = 10
                 *   range     -> 8..10
                 */
                if (upperNeighbourParagraphIndex !== null) {
                    const upperNeighbourBracketEndParagraphIndex = Math.min(upperNeighbourParagraphIndex + ParagraphLocator.BRACKET_ONE_SIDED_SPAN, lastIndex);
                    return range(upperNeighbourParagraphIndex, upperNeighbourBracketEndParagraphIndex);
                }
                /**
                 * WHEN only the lower neighbour survives
                 *   CONSEQUENCE
                 *     Search upward from that neighbour, floored at paragraph 0.
                 *
                 * EXAMPLE
                 *   below -> 2
                 *   span  -> 3
                 *   start -> max(-1, 0) = 0
                 *   range -> 0..2
                 */
                if (lowerNeighbourParagraphIndex !== null) {
                    const lowerNeighbourBracketStartParagraphIndex = Math.max(lowerNeighbourParagraphIndex - ParagraphLocator.BRACKET_ONE_SIDED_SPAN, 0);
                    return range(lowerNeighbourBracketStartParagraphIndex, lowerNeighbourParagraphIndex);
                }
                return null;
            };
            const bracketParagraphIndexes = bracketRegion(anchor);
            /**
             * WHEN neighbour ids do not produce a usable bracket region
             *   CONSEQUENCE
             *     Return null.
             *
             * NULL
             *   Neighbour bracketing has no bounded search region here.
             *   Caller should continue to later recovery tiers.
             */
            if (!bracketParagraphIndexes) return null;
            const toBracketResolvedLocation = (paragraphIndex: number): ParagraphLocation => ({ status: "resolved", paragraphIndex, layer: "accepted", resolutionPath: "neighbour_bracket" });
            const exactMatchParagraphIndexes = bracketParagraphIndexes.filter((index) => this.acceptedParagraphTexts[index] === target);
            /**
             * WHEN exactly one bracketed paragraph matches the remembered text verbatim
             *   CONSEQUENCE
             *     Resolve to that paragraph.
             *
             * EXAMPLE
             *   exact    -> [9]
             *   exact[0] -> 9
             */
            if (exactMatchParagraphIndexes.length === 1) return toBracketResolvedLocation(exactMatchParagraphIndexes[0]);
            const containmentMatchParagraphIndexes = bracketParagraphIndexes.filter((index) => isMergedParagraphContainmentMatch(this.acceptedParagraphTexts[index], target));
            /**
             * WHEN exactly one bracketed paragraph wholly contains the remembered text
             *   CONSEQUENCE
             *     Resolve to that merged paragraph.
             *
             * EXAMPLE
             *   containing    -> [10]
             *   containing[0] -> 10
             */
            if (containmentMatchParagraphIndexes.length === 1) return toBracketResolvedLocation(containmentMatchParagraphIndexes[0]);
            const contextConfirmedBracketCandidates = bracketParagraphIndexes.filter((index) =>
                ParagraphLocator.allRememberedContextSidesMatch(this.contextAt(anchor, this.acceptedWindows, index))
                && ParagraphLocator.signalStrength(target, this.acceptedParagraphTexts[index]) >= ParagraphLocator.FUZZY_CANDIDATE_FLOOR);
            /**
             * WHEN exactly one bracketed paragraph has confirming context and enough
             *   similarity
             *   CONSEQUENCE
             *     Resolve to that edited-in-place paragraph.
             *
             * EXAMPLE
             *   framed    -> [11]
             *   framed[0] -> 11
             */
            if (contextConfirmedBracketCandidates.length === 1) return toBracketResolvedLocation(contextConfirmedBracketCandidates[0]);
            return null;
            /**
             * Whether the candidate holds the whole remembered text, the remembered text being long
             * enough to be told apart.
             *
             * WHY the redline diff's words
             *   Cut at whitespace, a Japanese, Chinese or Thai clause was one word, under the
             *   minimum of four however long it ran, so a clause merged into its neighbour was
             *   never found by containment.
             *
             * EXAMPLE
             *   target "本契約は、両当事者が署名した日から効力を生じ、一年間存続する。" -> 21 words, contained -> true
             *   target "一年間存続"                                            -> 3 words, under 4  -> false
             *   target "for one year."                                         -> 3 words, under 4  -> false
             */
            function isMergedParagraphContainmentMatch(candidateText: string, target: string): boolean {
                return WordTokenizer.diffUnits(target).length >= ParagraphLocator.CONTAINMENT_MINIMUM_WORDS && candidateText.includes(target);
            }
        };
        /** The paragraph that still carries the remembered id, trusted when its text reads as remembered or drifted within reason. */
        const resolveAtKnownId = (anchor: ParagraphAnchor, target: string, paragraphIndex: number): ParagraphLocation | null => {
            const toKnownIdResolvedLocation = (layer: DocumentLayer, resolutionPath: ResolutionPath): ParagraphLocation => ({ status: "resolved", paragraphIndex, layer, resolutionPath });
            /**
             * WHEN the paragraph with the remembered id still has the remembered text in the
             *   accepted layer
             *   CONSEQUENCE
             *     Resolve as `acceptedParagraphTexts` with path `id_unchanged`.
             */
            if (this.acceptedParagraphTexts[paragraphIndex] === target) return toKnownIdResolvedLocation("accepted", "id_unchanged");
            /**
             * WHEN the remembered text survives only in the original layer at that id
             *   CONSEQUENCE
             *     Resolve as `allChangesRejectedParagraphTexts` with path `id_text_changed`.
             *
             * WHY
             *   The paragraph kept its id, but the accepted text no longer reads as anchored.
             */
            if (this.allChangesRejectedParagraphTexts[paragraphIndex] === target) return toKnownIdResolvedLocation("original", "id_text_changed");
            const acceptedLayerContextMatch = this.contextAt(anchor, this.acceptedWindows, paragraphIndex);
            const acceptedLayerMatchesRememberedContext = ParagraphLocator.allRememberedContextSidesMatch(acceptedLayerContextMatch);
            /**
             * WHEN the remembered id survives and both remembered context sides still confirm
             *   this accepted-layer paragraph
             *   CONSEQUENCE
             *     Trust the id despite text edits and resolve `id_text_changed`.
             */
            if (acceptedLayerMatchesRememberedContext) return toKnownIdResolvedLocation("accepted", "id_text_changed");
            const acceptedLayerSignalStrength = ParagraphLocator.signalStrength(target, this.acceptedParagraphTexts[paragraphIndex]);
            const acceptedLayerMeetsFuzzyCandidateFloor = acceptedLayerSignalStrength >= ParagraphLocator.FUZZY_CANDIDATE_FLOOR;
            /**
             * WHEN the remembered id survives and accepted text similarity stays above the
             *   fuzzy candidate floor
             *   CONSEQUENCE
             *     Trust the id despite wording drift and resolve `id_text_changed`.
             */
            if (acceptedLayerMeetsFuzzyCandidateFloor) return toKnownIdResolvedLocation("accepted", "id_text_changed");
            return null;
        };
        const targetParagraphText = WordTokenizer.collapse(anchor.text);
        const idHits = this.indexesOfParaId(anchor.paraId);
        /**
         * WHEN the remembered `paraId` matches exactly one live paragraph
         *   CONSEQUENCE
         *     Try the known-id tier.
         *
         * WHY
         *   Zero hits means the id was lost. More than one hit means the id was duplicated.
         */
        if (idHits.length === 1) {
            /**
             * EXAMPLE
             *   idHits    -> [14]
             *   idHits[0] -> 14
             *
             * RETURNS
             *   The known-id tier's answer for the sole paragraph still carrying that `paraId`.
             */
            const knownIdLocation = resolveAtKnownId(anchor, targetParagraphText, idHits[0]);
            /**
             * WHEN the known-id tier resolved the paragraph
             *   CONSEQUENCE
             *     Return it now.
             *
             * WHY
             *   Weaker text-based tiers must not override a trusted surviving id.
             */
            if (knownIdLocation) return knownIdLocation;
        }
        /**
         * WHEN the remembered `paraId` is missing or duplicated
         *   CONSEQUENCE
         *     Fall back to neighbour bracketing.
         *
         * WHY
         *   In both cases, id-based recovery is not trustworthy.
         */
        if (idHits.length !== 1) {
            const bracketResolvedLocation = resolveByBracket(anchor, targetParagraphText);
            /**
             * WHEN surviving neighbours uniquely fence in one paragraph
             *   CONSEQUENCE
             *     Return that bracketed match now.
             *
             * WHY
             *   A neighbour bracket is stronger evidence than later unrestricted text search.
             */
            if (bracketResolvedLocation) return bracketResolvedLocation;
        }
        const sectionParagraphIndexes = paragraphIndexesUnderHeadingPath(anchor.headingPath);
        /**
         * WHEN the remembered heading still narrows to a real subsection
         *   CONSEQUENCE
         *     Run the text tiers inside that section only.
         */
        if (sectionParagraphIndexes) {
            const sectionTierHit = resolveByTextTiers(scoreParagraphCandidates(anchor, targetParagraphText, sectionParagraphIndexes, idHits, memberIndex));
            /**
             * WHEN a section-restricted tier names one paragraph
             *   CONSEQUENCE
             *     Return it as `resolved`.
             *
             * WHY
             *   A win inside the remembered heading is stronger than a document-wide search.
             */
            if (sectionTierHit) return { status: "resolved", ...sectionTierHit };
        }
        const documentWideScoredCandidates = scoreParagraphCandidates(anchor, targetParagraphText, this.locatableParagraphs.map((_, index) => index), idHits, memberIndex);
        const documentWideTierHit = resolveByTextTiers(documentWideScoredCandidates);
        /**
         * WHEN a document-wide deterministic tier names one paragraph
         *   CONSEQUENCE
         *     Return it as `resolved`.
         *
         * WHY
         *   The rank gate runs only after exact, context, fuzzy, and semantic tiers fail
         *   to produce a unique answer.
         */
        if (documentWideTierHit) return { status: "resolved", ...documentWideTierHit };
        return rankAndGate(documentWideScoredCandidates);
        /** Exact, then context-confirmed, then fuzzy, then semantic; the first tier to name one paragraph wins. */
        function resolveByTextTiers(scored: ScoredCandidate[]): TierHit | null {
            /**
             * CASES
             *   - candidates.length === 1 -> return that only candidate as the tier hit
             *   - otherwise               -> return null
             *
             * EXAMPLE
             *   one candidate at paragraph 22 in `original`
             *   candidates[0].paragraphIndex -> 22
             *   candidates[0].layer          -> `original`
             */
            const uniqueHit = (candidates: ScoredCandidate[], resolutionPath: ResolutionPath): TierHit | null =>
                candidates.length === 1 ? { paragraphIndex: candidates[0].paragraphIndex, layer: candidates[0].layer, resolutionPath } : null;
            const tierExact = (): TierHit | null => uniqueHit(scored.filter((candidate) => candidate.exact), "text_tier_exact");
            const tierContext = (): TierHit | null => uniqueHit(
                scored.filter((candidate) => ParagraphLocator.allRememberedContextSidesMatch(candidate.context) && (candidate.exact || candidate.similarity >= ParagraphLocator.FUZZY_CANDIDATE_FLOOR)),
                "text_tier_context",
            );
            // A single fuzzy candidate with no identity behind it must be very close: one
            // reworded paragraph that happens to share half its words is not the target.
            const tierFuzzy = (): TierHit | null => {
                const contextSupportedFuzzyCandidates = scored.filter((candidate) => candidate.similarity >= ParagraphLocator.FUZZY_CANDIDATE_FLOOR && hasAnyMatchingContextSide(candidate.context));
                const identityAbsent = !scored.some((candidate) => candidate.idMatches);
                const hasOneContextSupportedFuzzyCandidate = contextSupportedFuzzyCandidates.length === 1;
                /**
                 * A lone fuzzy match with no id to back it is held to a stricter floor: one
                 * reworded paragraph that happens to share half its words is not the target.
                 *
                 * CASES
                 *   - several candidates, or none      -> not this rule
                 *   - a candidate carries the anchor's id -> not this rule: identity vouches for it
                 *   - otherwise                        -> reject when the single candidate ([0]) sits under the strict floor
                 */
                const singleContextSupportedFuzzyCandidateFallsBelowStrictFloor = (): boolean => {
                    if (!hasOneContextSupportedFuzzyCandidate) return false;
                    if (!identityAbsent) return false;
                    return contextSupportedFuzzyCandidates[0].similarity < ParagraphLocator.FUZZY_SINGLE_CANDIDATE_FLOOR;
                };
                const singleContextSupportedFuzzyCandidateShouldBeRejectedWithoutIdentity = singleContextSupportedFuzzyCandidateFallsBelowStrictFloor();
                /**
                 * WHEN exactly one fuzzy candidate is supported by context, but no id evidence
                 *   exists and similarity is below the stricter single-candidate floor
                 *   CONSEQUENCE
                 *     Return null.
                 *
                 * WHY
                 *   A lone near-match without identity support may be coincidence.
                 */
                if (singleContextSupportedFuzzyCandidateShouldBeRejectedWithoutIdentity) return null;
                return ParagraphLocator.uniqueByScore(contextSupportedFuzzyCandidates, (candidate) => candidate.similarity, ParagraphLocator.FUZZY_UNIQUENESS_MARGIN, "text_tier_fuzzy");
                /** At least one side matches. */
                function hasAnyMatchingContextSide(context: ContextMatch): boolean {
                    return context.before === true || context.after === true;
                }
            };
            // The last deterministic tier: a paraphrase the word-level tiers cannot see,
            // named only when one paragraph leads the rest by the whole margin.
            const tierSemantic = (): TierHit | null => {
                const semanticParaphraseCandidates = scored.filter(
                    (candidate) => candidate.semantic !== null && candidate.semantic >= ParagraphLocator.SEMANTIC_CANDIDATE_FLOOR,
                );
                return ParagraphLocator.uniqueByScore(
                    semanticParaphraseCandidates,
                    (candidate) => candidate.semantic ?? 0,
                    ParagraphLocator.SEMANTIC_UNIQUENESS_MARGIN,
                    "text_tier_semantic",
                );
            };
            for (const runTier of [tierExact, tierContext, tierFuzzy, tierSemantic]) {
                const tierHit = runTier();
                /**
                 * WHEN the current text tier uniquely identifies one paragraph
                 *   CONSEQUENCE
                 *     Return it immediately.
                 *
                 * WHY
                 *   Tier order is strongest to weakest; later tiers must not override an earlier
                 *   deterministic win.
                 */
                if (tierHit) return tierHit;
            }
            return null;
        }
    }

    /**
     * True when a live paragraph is one of the paragraphs the anchor remembers beside
     * its own (`neighbourAbove`, `neighbourBelow`).
     *
     * CASES, per remembered neighbour
     *   - the live paragraph carries the neighbour's paraId                    -> true
     *   - it reads as the neighbour's text (`WordTokenizer.readsTheSame`), and
     *     the neighbour's text does not read as the anchor's own              -> true
     *   - the neighbour's text holds no word (a blank spacer)                  -> not compared by text:
     *                                                                            every blank reads the same
     *   - otherwise                                                            -> false
     *
     * EXAMPLE
     *   anchor 8.1 "Severity 1 … four (4) hours.", neighbourBelow 8.2 "Severity 2 … two (2) days."
     *   live paragraph 50 reads "Severity 2 … two (2) days."   -> true: it is 8.2, not 8.1
     *   live paragraph 51 reads "Severity 1 … two (2) hours."  -> false
     */
    private isRememberedNeighbourOf(anchor: ParagraphAnchor, anchorText: string, paragraphIndex: number): boolean {
        const liveParagraph = this.locatableParagraphs[paragraphIndex];
        const liveAcceptedText = this.acceptedParagraphTexts[paragraphIndex];
        const rememberedNeighbours = [anchor.neighbourAbove, anchor.neighbourBelow].flatMap((neighbour) => (neighbour === null ? [] : [neighbour]));
        return rememberedNeighbours.some((neighbour) => {
            const carriesNeighbourId = neighbour.paraId !== null && neighbour.paraId === liveParagraph.paraId;
            const neighbourHoldsWords = WordTokenizer.words(neighbour.text).length > 0;
            const readsAsNeighbourNotAnchor = neighbourHoldsWords && WordTokenizer.readsTheSame(neighbour.text, liveAcceptedText) && !WordTokenizer.readsTheSame(neighbour.text, anchorText);
            return carriesNeighbourId || readsAsNeighbourNotAnchor;
        });
    }

    private rankScore(candidate: ScoredCandidate): number {
        let rankGateScore = ParagraphLocator.RANK_SIMILARITY_WEIGHT * candidate.similarity;
        /**
         * WHEN the remembered `before` context matches on this layer
         *   CONSEQUENCE
         *     Add one context-side weight to the rank score.
         */
        if (candidate.context.before === true) rankGateScore += ParagraphLocator.RANK_CONTEXT_SIDE_WEIGHT;
        /**
         * WHEN the remembered `after` context matches on this layer
         *   CONSEQUENCE
         *     Add one context-side weight to the rank score.
         */
        if (candidate.context.after === true) rankGateScore += ParagraphLocator.RANK_CONTEXT_SIDE_WEIGHT;
        /**
         * WHEN the candidate is under the same heading path
         *   CONSEQUENCE
         *     Add the heading weight to the rank score.
         *
         * WHY
         *   Missing heading evidence and contradictory heading evidence get no bonus.
         */
        if (candidate.headingPathMatches === true) rankGateScore += ParagraphLocator.RANK_HEADING_WEIGHT;
        return rankGateScore;
    }

    private contextAt(anchor: ParagraphAnchor, windows: LayerWindows, paragraphIndex: number): ContextMatch {
        /**
         * The remembered context cut into words the way the windows are, and held to the
         * window's length: the last `CONTEXT_WINDOW_WORDS` words before the paragraph, the
         * first after it.
         *
         * WHY cut again here
         *   The add-in sends the last eight whitespace words of the paragraph above. A
         *   Japanese paragraph is one whitespace word, so it sends the whole paragraph,
         *   while the window holds its last eight words: compared as sent, the two never
         *   matched.
         *
         * EXAMPLE
         *   contextBefore "本契約は、注文書に記載された供給者と顧客との間で締結される。", 23 words
         *   slice(-8) -> "と" "の" "間" "で" "締結" "さ" "れる" "。", the 8 words that end it, as the window reads them
         *   contextBefore "and the Customer named in the order form." -> its 8 words, as before
         */
        const rememberedContextBefore = ParagraphLocator.contextWordKeysOf(anchor.contextBefore).slice(-CONTEXT_WINDOW_WORDS).join(" ");
        const rememberedContextAfter = ParagraphLocator.contextWordKeysOf(anchor.contextAfter).slice(0, CONTEXT_WINDOW_WORDS).join(" ");
        return {
            before: rememberedContextBefore ? windows.before[paragraphIndex] === rememberedContextBefore : null,
            after: rememberedContextAfter ? windows.after[paragraphIndex] === rememberedContextAfter : null,
        };
    }

    /**
     * The words a context is counted and compared in: the redline diff's units
     * (`WordTokenizer.diffUnits`), each as its comparison key, so a Japanese, Chinese or
     * Thai paragraph gives its words and not one whole run, and the composed and
     * decomposed spellings of a character compare equal.
     *
     * EXAMPLE
     *   "and the Customer named"      -> "and" "the" "Customer" "named"
     *   "締結される。"                  -> "締結" "さ" "れる" "。"
     */
    private static contextWordKeysOf(text: string): string[] {
        return WordTokenizer.diffUnits(text).map((word) => WordTokenizer.comparisonKey(word.text));
    }

    private indexesOfParaId(paraId: string | null): number[] {
        /**
         * WHEN the anchor remembered no paragraph id
         *   CONSEQUENCE
         *     Return an empty hit list.
         *
         * RETURNS
         *   No id-based matches. Caller should fall through to neighbour or text evidence.
         */
        if (paraId === null) return [];
        /**
         * CASES
         *   - paragraph.paraId === paraId -> keep [index]
         *   - otherwise                   -> keep []
         *
         * EXAMPLE
         *   matches at paragraphs 3 and 9 -> [3, 9]
         *
         * WHY
         *   Duplicates are preserved so callers can reject ambiguous ids.
         */
        return this.locatableParagraphs.flatMap((paragraph, index) => (paragraph.paraId === paraId ? [index] : []));
    }

    /** Every remembered side matches, and at least one side was remembered. */
    private static allRememberedContextSidesMatch(context: ContextMatch): boolean {
        const rememberedContextSideMatches = [context.before, context.after].filter((side): side is boolean => side !== null);
        return rememberedContextSideMatches.length > 0 && rememberedContextSideMatches.every(Boolean);
    }

    private static signalStrength(target: string, text: string): number {
        /**
         * CASES
         *   - text === target -> 1
         *   - otherwise       -> TextSimilarity.score(text, target)
         *
         * WHY
         *   An exact match is perfect signal strength and should not depend on floating
         *   point similarity scoring.
         */
        return text === target ? 1 : TextSimilarity.score(text, target);
    }

    private static headingPathsMatchExactly(left: string[] | null, right: string[]): boolean {
        return left !== null && left.length === right.length && left.every((component, index) => component === right[index]);
    }

    private static uniqueByScore(
        candidates: ScoredCandidate[],
        scoreOf: (candidate: ScoredCandidate) => number,
        margin: number,
        resolutionPath: ResolutionPath,
    ): TierHit | null {
        /**
         * WHEN this tier produced no candidates
         *   CONSEQUENCE
         *     Return null.
         *
         * NULL
         *   No unique hit from this tier. Caller should continue to the next weaker rule.
         */
        if (candidates.length === 0) return null;
        const scoreRankedCandidates = [...candidates].sort((left, right) => scoreOf(right) - scoreOf(left));
        const hasOneScoreRankedCandidate = scoreRankedCandidates.length === 1;
        /**
         * CASES
         *   - one candidate         -> it leads by default
         *   - two or more           -> the best ([0], sorted descending) must beat the runner-up ([1]) by `margin`
         */
        const scoreRankedCandidatesHaveUniqueLeader = (): boolean => {
            if (hasOneScoreRankedCandidate) return true;
            const topScoreRankedCandidate = scoreRankedCandidates[0];
            const runnerUpScoreRankedCandidate = scoreRankedCandidates[1];
            const topScoreValue = scoreOf(topScoreRankedCandidate);
            const runnerUpScoreValue = scoreOf(runnerUpScoreRankedCandidate);
            return topScoreValue - runnerUpScoreValue >= margin;
        };
        /**
         * CASES
         *   - ranked.length === 1                          -> accept the only candidate
         *   - top score - second score >= margin           -> accept the leader
         *   - otherwise                                    -> not unique enough
         *
         * EXAMPLE
         *   scores  -> [0.91, 0.76]
         *   margin  -> 0.1
         *   lead    -> 0.15
         *   result  -> ranked[0] wins
         *
         * EXAMPLE
         *   scores  -> [0.91, 0.85]
         *   margin  -> 0.1
         *   lead    -> 0.06
         *   result  -> no unique hit
         */
        if (scoreRankedCandidatesHaveUniqueLeader()) {
            /**
             * RETURNS
             *   The highest-scoring candidate for this tier after descending sort.
             *
             * EXAMPLE
             *   ranked[0] is the best-scoring paragraph under this tier's rule.
             */
            return { paragraphIndex: scoreRankedCandidates[0].paragraphIndex, layer: scoreRankedCandidates[0].layer, resolutionPath };
        }
        return null;
    }
}
